// ChapterSlideModal.jsx
import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Upload,
  Button,
  message,
  Space,
  Tooltip,
} from "antd";
import {
  UploadOutlined,
  EditOutlined,
  CheckCircleTwoTone,
} from "@ant-design/icons";
import axios from "axios";
import { FaPlus } from "react-icons/fa6";
import "./../style/common.css";
import PhysioBuzzEditor from "../pages/PhysioBuzzEditor";

/* =========================
   Helpers (same as yours)
   ========================= */

const sanitizeStepTags = (htmlRaw = "") => {
  let html = String(htmlRaw);

  // Keep markers tight against content (remove <br> around them)
  html = html
    .replace(/<br\s*\/?>\s*(\[(?:STEP|STEPNI)\])\s*/gi, "$1")
    .replace(/\s*(\[(?:STEP|STEPNI)\])\s*<br\s*\/?>/gi, "$1");

  // DO NOT collapse duplicate markers anymore
  // (removed the collapse() calls)

  // Drop "closing" variants if they appear; your pipeline uses [STEP] ... [STEP]
  html = html.replace(/\[\/STEP\]|\[\/STEPNI\]/gi, "");

  // Remove empty blocks
  html = html.replace(/<(p|div)\b[^>]*>(\s|&nbsp;|<br\s*\/?>)*<\/\1>/gi, "");

  return html.trim();
};

const stripMarkers = (s, tag) =>
  s
    .replace(new RegExp(`^\\s*\\[${tag}\\]\\s*`, "i"), "")
    .replace(new RegExp(`\\s*\\[${tag}\\]\\s*$`, "i"), "");

const isWrapped = (s, tag) =>
  new RegExp(`^\\s*\\[${tag}\\]([\\s\\S]*?)\\[${tag}\\]\\s*$`, "i").test(
    s.replace(/\[\/?(STEP|STEPNI)\]/gi, (m) => (m.includes("/") ? "" : m))
  );

const wrapOnce = (s, tag) => `[${tag}]${stripMarkers(s, tag)}[${tag}]`;

const liftMarkersOutsideBlocks = (htmlIn = "") => {
  let html = String(htmlIn);
  const BLOCKS = ["h1", "h2", "h3", "h4", "h5", "h6", "p", "blockquote", "div"];
  const TAGS = ["STEP", "STEPNI"];

  BLOCKS.forEach((blk) => {
    TAGS.forEach((tag) => {
      const reBoth = new RegExp(
        `<${blk}([^>]*)>\\s*\\[${tag}\\]\\s*([\\s\\S]*?)\\s*\\[${tag}\\]\\s*<\\/${blk}>`,
        "gi"
      );
      html = html.replace(reBoth, (_m, attrs, inner) => {
        return `[${tag}]<${blk}${attrs}>${inner}</${blk}>[${tag}]`;
      });
    });
  });

  BLOCKS.forEach((blk) => {
    TAGS.forEach((tag) => {
      const reLead = new RegExp(
        `<${blk}([^>]*)>\\s*\\[${tag}\\]\\s*([\\s\\S]*?)<\\/${blk}>`,
        "gi"
      );
      html = html.replace(reLead, (_m, attrs, inner) => {
        return `[${tag}]<${blk}${attrs}>${inner}</${blk}>[${tag}]`;
      });
    });
  });

  BLOCKS.forEach((blk) => {
    TAGS.forEach((tag) => {
      const reTrail = new RegExp(
        `<${blk}([^>]*)>([\\s\\S]*?)\\s*\\[${tag}\\]\\s*<\\/${blk}>`,
        "gi"
      );
      html = html.replace(reTrail, (_m, attrs, inner) => {
        return `[${tag}]<${blk}${attrs}>${inner}</${blk}>[${tag}]`;
      });
    });
  });

  return html;
};

const submitNormalize = (htmlIn = "") => {
  let html = sanitizeStepTags(htmlIn);

  html = html.replace(
    /<h(\d)([^>]*)>(.*?)<\/h\1>/gis,
    (match, level, attrs, inner) => {
      if (/\[STEP\]/i.test(inner)) {
        const innerClean = inner.replace(/\[STEP\]/gi, "");
        return wrapOnce(`<h${level}${attrs}>${innerClean}</h${level}>`, "STEP");
      }
      return match;
    }
  );

  html = html.replace(/<p([^>]*)>(.*?)<\/p>/gis, (match, attrs, inner) => {
    if (/\[STEP\]/i.test(inner)) {
      const clean = inner.replace(/\[STEP\]/gi, "");
      const block = `<p${attrs}>${clean}</p>`;
      return isWrapped(block, "STEP") ? block : wrapOnce(block, "STEP");
    }
    if (/\[STEPNI\]/i.test(inner)) {
      const clean = inner.replace(/\[STEPNI\]/gi, "");
      const block = `<p${attrs}>${clean}</p>`;
      return isWrapped(block, "STEPNI") ? block : wrapOnce(block, "STEPNI");
    }
    return match;
  });

  html = html.replace(/<ul([^>]*)>(.*?)<\/ul>/gis, (m, attrs, inner) => {
    if (!/\[STEPNI\]/i.test(inner)) return m;
    const cleanedInner = inner.replace(/\[STEPNI\]/gi, "");
    const block = `<ul${attrs}>${cleanedInner}</ul>`;
    return isWrapped(block, "STEPNI") ? block : wrapOnce(block, "STEPNI");
  });

  return sanitizeStepTags(html);
};

// Build .txt file from content_text
const buildTxtFile = (text) => {
  const filename = `chapter_slide_${Date.now()}.txt`;
  try {
    return {
      file: new File([text], filename, { type: "text/plain" }),
      name: filename,
    };
  } catch {
    return { file: new Blob([text], { type: "text/plain" }), name: filename };
  }
};

/* =========================
   Component
   ========================= */

const ChapterSlideModal = ({
  visible,
  onClose,
  chapter,
  editData,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [editorContent, setEditorContent] = useState(""); // normalized HTML preview (optional)
  const [contentFile, setContentFile] = useState(null); // the .txt to upload as "content"
  const [contentFileList, setContentFileList] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imageList, setImageList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false); // controls PhysioBuzzEditor modal
  const [hasEditorValue, setHasEditorValue] = useState(false);

  const editMode = editData?.id != null;

  useEffect(() => {
    if (chapter || editMode) {
      form.setFieldsValue({
        chapter_id: chapter?.id || editData?.chapter_id,
        order: editData?.order || 1,
        animation_type: editData?.animation_type || "",
        image_position: editData?.image_position || "",
      });

      const initial = editData?.content_text || "";
      const normalized = sanitizeStepTags(initial);

      setEditorContent(normalized);
      setHasEditorValue(!!normalized);

      // no contentFile initially in edit mode — will be created when user exports from editor
      setContentFile(null);
      setContentFileList([]);

      if (editData?.image) {
        setImageList([
          {
            uid: "-1",
            name: "existing_image.jpg",
            status: "done",
            url: editData.image,
          },
        ]);
      } else {
        setImageList([]);
      }
      setImageFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter, editData, editMode]);

  // When the PhysioBuzzEditor exports, receive the text and file/Blob
  // When the PhysioBuzzEditor exports, receive the text and file/Blob
  const handleEditorExport = (_fileOrBlob, rawText, filename) => {
    // ✅ Preserve exactly what editor produced (keeps [STEP] [STEP] sequences)
    const preserved = String(rawText || "");

    let file;
    try {
      file = new File(
        [preserved],
        filename || `chapter_slide_${Date.now()}.txt`,
        { type: "text/plain" }
      );
    } catch {
      file = new Blob([preserved], { type: "text/plain" });
      file.name = filename || `chapter_slide_${Date.now()}.txt`;
    }

    // For preview, show exactly what will be uploaded
    setEditorContent(preserved);
    setHasEditorValue(!!preserved);
    setContentFile(file);
    setContentFileList([
      {
        uid: "content-txt",
        name: filename || "chapter_slide.txt",
        status: "done",
      },
    ]);

    message.success("Editor content attached as .txt");
    setEditorOpen(false);
  };

  const handleFinish = async (values) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      // Fallback if user didn’t export a file from the editor (use preview HTML to generate .txt)
      const cleanedContent = submitNormalize(
        liftMarkersOutsideBlocks(editorContent || "")
      );

      const formData = new FormData();
      formData.append("chapter_id", values.chapter_id);
      formData.append("order", values.order);
      formData.append("animation_type", values.animation_type || "");
      if (values.image_position)
        formData.append("image_position", values.image_position);

      if (contentFile) {
        formData.append(
          "content",
          contentFile,
          contentFile.name || `chapter_slide_${Date.now()}.txt`
        );
      } else {
        // create a file from cleanedContent
        const { file, name } = buildTxtFile(cleanedContent);
        formData.append("content", file, name);
      }

      if (imageFile) formData.append("image", imageFile);

      const baseUrl = "https://pranafreshharvest.com/api/chapter-slides";
      const url =
        editMode && editData?.id ? `${baseUrl}/${editData.id}` : baseUrl;

      await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      message.success(`Slide ${editMode ? "updated" : "added"} successfully`);
      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error(err);
      message.error(`Failed to ${editMode ? "update" : "add"} slide`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Main Slide Modal */}
      <Modal
        open={visible}
        title={
          <span>
            {editMode ? "Update Slide" : "Add Slide"}{" "}
            {chapter && (
              <>
                for:{" "}
                <span
                  style={{
                    color: "#532547",
                    fontWeight: 600,
                    fontSize: "20px",
                  }}
                >
                  {chapter.title}
                </span>
              </>
            )}
          </span>
        }
        onCancel={onClose}
        // onOk={() => form.submit()}
        maskClosable={false}
        width={900}
        confirmLoading={loading}
        footer={
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={loading}
          >
            <FaPlus /> Add Slide
          </Button>
        }
      >
        <Form layout="vertical" form={form} onFinish={handleFinish}>
          <Form.Item name="chapter_id" label="Chapter ID" hidden>
            <Input disabled />
          </Form.Item>

          <Form.Item
            name="order"
            label="Slide Order"
            rules={[{ required: true }]}
            className="slider-order-modal"
          >
            <Input type="number" />
          </Form.Item>

          <Form.Item name="image" label="Image">
            <Upload
              beforeUpload={(file) => {
                setImageFile(file);
                setImageList([file]);
                return false;
              }}
              fileList={imageList}
              onRemove={() => {
                setImageFile(null);
                setImageList([]);
              }}
              accept="image/*"
              maxCount={1}
              listType="picture"
            >
              <Button icon={<UploadOutlined />}>Select Image</Button>
            </Upload>
          </Form.Item>

          <Form.Item name="image_position" label="Image Position">
            <Select placeholder="Select position" allowClear>
              <Select.Option value="T">Top</Select.Option>
              <Select.Option value="B">Bottom</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="animation_type" label="Animation Type">
            <Select placeholder="Select animation type" allowClear>
              {[
                "none",
                "fade",
                "scale",
                "rotation",
                "slide",
                "slide_left",
                "slide_right",
                "slide_up",
                "slide_down",
                "zoom_in",
                "zoom_out",
              ].map((value) => (
                <Select.Option key={value} value={value}>
                  {value}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* Editor Controls */}
      {/* In your component (ensure you have access to `form`, `hasEditorValue`, `editorContent`) */}
<Form.Item
  label="Slide Content"
  name="content"
  rules={[
    {
      validator: (_, v) => {
        const ok =
          hasEditorValue &&
          typeof editorContent === "string" &&
          editorContent.trim().length > 0;
        return ok ? Promise.resolve() : Promise.reject(
          new Error("Required slide content before saving.")
        );
      },
    },
  ]}
>
  <>
    <Space>
      <Button
        type="primary"
        icon={<EditOutlined />}
        onClick={() => setEditorOpen(true)}
      >
        Click To Add Slide Content
      </Button>

      {hasEditorValue && (
        <Tooltip title="Editor content attached">
          <CheckCircleTwoTone twoToneColor="#52c41a" />
        </Tooltip>
      )}
    </Space>

    {/* Attached .txt preview (optional) */}
    <div style={{ marginTop: 12 }}>
      <Upload
        fileList={contentFileList}
        beforeUpload={() => false}
        showUploadList={{ showRemoveIcon: true }}
        onRemove={() => {
          setContentFile(null);
          setContentFileList([]);
          setHasEditorValue(false);
          setEditorContent("");
          // keep form state in sync so validation re-runs
          form.setFieldsValue({ content: "" });
          form.validateFields(["content"]);
        }}
      />
    </div>

    {/* Snippet Preview (optional) */}
    {hasEditorValue && (
      <div
        style={{
          marginTop: 8,
          padding: 10,
          border: "1px dashed #d9d9d9",
          borderRadius: 6,
          maxHeight: 120,
          overflow: "auto",
          background: "#fafafa",
          fontSize: 12,
          color: "#555",
        }}
      >
        <strong>Preview</strong>
        <div style={{ whiteSpace: "pre-wrap" }}>{String(editorContent)}</div>
      </div>
    )}
  </>
</Form.Item>

        </Form>
      </Modal>

      {/* PhysioBuzzEditor in a modal (no iframe) */}
      <Modal
        open={editorOpen}
        // title="Physio Buzz Editor"
        onCancel={() => setEditorOpen(false)}
        footer={null}
        width={1000}
        destroyOnClose
        maskClosable={false}
      >
        <PhysioBuzzEditor
          initialHtml={editorContent || ""}
          onExport={(fileOrBlob, text, filename) =>
            handleEditorExport(fileOrBlob, text, filename)
          }
        />
      </Modal>
    </>
  );
};

export default ChapterSlideModal;
